import {
  WorkflowRun,
  WorkflowRunStep,
  WorkflowTemplate,
} from '@digital-twins/shared-types'

import {
  readFollowUpKind,
  readFollowUpStem,
} from './gateFollowUpKeys'

/**
 * What a gate actually puts in front of a reviewer.
 *
 * A `gate` step's config names STEP KEYS (`artifacts: ['write_brief',
 * 'draft_content']`), not artifact slugs, and one of those steps may have fanned
 * out into six drafts. Flattening that into a single ordered list is the whole
 * job here, so the card list and the submitted payload are indexed the same way
 * and `run-gate-artifact-3` always means the same artifact as
 * `run-gate-edit-3`.
 */

export interface GateArtifactEntry {
  /** Stable identity across a re-render: `stepKey#slug#index`. */
  key: string
  /**
   * The PRODUCING step's key. The gate payload is
   * `Record<stepKey, WorkflowStepOutput>`, so an entry that cannot name the step
   * it came from is an entry that cannot be submitted: the flattening below is
   * exactly where that identity used to be thrown away.
   */
  stepKey: string
  /** The producing step's `output` slug. Travels back in the gate payload. */
  slug: string
  /** 0-based fan-out position, or null for a single-artifact step. */
  index: number | null
  title: string
  /** The body as it stands on the run: an earlier edit wins over the draft. */
  body: string
}

/**
 * Includes the step key because a gate flattens several steps into one list and
 * two of them may declare the same output slug. Without it, the two collapse
 * onto one card key and an edit lands on the wrong artifact.
 */
export function gateArtifactKey(
  stepKey: string,
  slug: string,
  index: number | null,
): string {
  return `${stepKey}#${slug}#${index ?? 0}`
}

/**
 * The gate config as it was PINNED onto the run.
 *
 * `workflow-run.service.ts` writes `input: { config: step }` onto every step row
 * at creation, and the server reads that same copy back (`readGateArtifactKeys`)
 * to decide which override keys it will accept. The fetched template is the
 * CURRENT one and may have been edited since the run started, so the pinned copy
 * wins wherever it exists and the template is the fallback.
 */
function pinnedGateConfig(
  gateStep: WorkflowRunStep,
): { artifacts: Array<string>; editable: boolean } | null {
  const input = gateStep.input
  const config = input ? input.config : null
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return null
  }

  const candidate = config as { kind?: unknown; artifacts?: unknown; editable?: unknown }
  if (candidate.kind !== 'gate' || !Array.isArray(candidate.artifacts)) {
    return null
  }

  return {
    artifacts: candidate.artifacts.filter(
      (value): value is string => typeof value === 'string',
    ),
    editable: candidate.editable === true,
  }
}

/**
 * The first line a collapsed card shows. Markdown furniture is stripped rather
 * than rendered: a card preview reading `## What changed overnight` shows the
 * author's markup instead of their sentence.
 */
export function firstLineOf(body: string): string {
  const line = body
    .split('\n')
    .map((raw) => raw.trim())
    .find((raw) => raw.length > 0)

  if (!line) {
    return ''
  }

  return line
    .replace(/^#{1,6}\s+/, '')
    .replace(/^[-*+]\s+/, '')
    .replace(/^\d+\.\s+/, '')
    .replace(/^>\s+/, '')
    .replace(/\*\*/g, '')
    .replace(/[*_`]/g, '')
    .trim()
}

function artifactsOf(step: WorkflowRunStep): Array<GateArtifactEntry> {
  // `outputOverride ?? output`, never `output` alone: a gate reopened after an
  // earlier edit must show what was approved, not the model's first draft.
  const output = step.outputOverride ?? step.output
  if (!output || output.kind !== 'artifacts') {
    return []
  }

  return output.artifacts.map((artifact) => ({
    key: gateArtifactKey(step.stepKey, artifact.slug, artifact.index ?? null),
    stepKey: step.stepKey,
    slug: artifact.slug,
    index: artifact.index ?? null,
    title: artifact.title,
    body: artifact.body,
  }))
}

/**
 * The gate's review list, in template order.
 *
 * Without the template the gate config is unreachable, so the fallback is every
 * artifact produced before the gate. That is a superset rather than a guess: it
 * can show one artifact too many, and it can never hide one the reviewer is
 * being asked to approve.
 */
export function collectGateArtifacts(
  run: WorkflowRun,
  gateStep: WorkflowRunStep,
  template: WorkflowTemplate | null,
): Array<GateArtifactEntry> {
  const stepsByKey = new Map<string, WorkflowRunStep>()
  for (const step of run.steps) {
    stepsByKey.set(step.stepKey, step)
  }

  const pinned = pinnedGateConfig(gateStep)
  const config = template?.steps.find(
    (candidate) => candidate.key === gateStep.stepKey,
  )
  const artifactKeys = pinned
    ? pinned.artifacts
    : config && config.kind === 'gate'
      ? config.artifacts
      : null

  if (artifactKeys) {
    return artifactKeys.flatMap((stepKey) => {
      const source = stepsByKey.get(stepKey)
      return source ? artifactsOf(source) : []
    })
  }

  return run.steps
    .filter((step) => step.stepIndex < gateStep.stepIndex)
    .flatMap(artifactsOf)
}

/**
 * Whether the gate's config allows editing. The pinned run copy wins; an absent
 * template then means read-only, because an override key the server did not
 * declare editable is a 400 rather than a rejected edit.
 */
export function isGateEditable(
  gateStep: WorkflowRunStep,
  template: WorkflowTemplate | null,
): boolean {
  const pinned = pinnedGateConfig(gateStep)
  if (pinned) {
    return pinned.editable
  }

  const config = template?.steps.find(
    (candidate) => candidate.key === gateStep.stepKey,
  )
  return config?.kind === 'gate' ? config.editable : false
}

/**
 * The machine tail an intake turn returns while a gate is open.
 *
 * Mirrors the frozen contract in `docs/plans/twin-test-conversational-intake.md`
 * verbatim. It is declared here rather than in the API client because the gate
 * is the only thing that may act on it, and Step 8 never edits the client.
 */
export interface GateRevisionTail {
  /**
   * Every status the intake route can answer with, `handoff` included: the
   * page calls `applyRevision` with whatever the turn produced, and anything
   * that is not `revision` asks the writer for nothing.
   */
  status: 'need_more' | 'direction' | 'revision' | 'handoff'
  /**
   * `revision` only: the model could not tell what to change, so nothing is
   * asked of the writer and the prose it streamed is the whole answer.
   */
  refused?: boolean
}

/** What `applyRevision` did. Returned for the caller's logs and for tests. */
export type GateRevisionOutcome =
  /** The writer was asked to redo the step. The dock says so until it lands. */
  | 'revising'
  /** Refused or malformed. Nothing was asked of the writer. */
  | 'ignored'
  /** The gate closed or advanced while the turn ran. Discarded, and said so. */
  | 'gate_moved'

/**
 * The gate's side of chat-driven revision, handed UP to the thread page so a
 * typed message can be routed into the open gate instead of the assistant.
 *
 * The page holds whatever it was last given. A null handle means there is
 * nothing to revise (no pending gate, a colleague's run, or a read-only gate)
 * and the message belongs to the ordinary assistant turn.
 *
 * CAPTURE IT AT SEND TIME. A turn outlives the gate that started it: the
 * handle may go null while the model is still writing, and calling
 * `applyRevision` on the captured handle is what produces the "gate already
 * closed" notice rather than silence. Nothing was sent to the server at any
 * point, so a discarded rewrite needs no undo.
 */
export interface GateRevisionHandle {
  /** The gate step this handle was minted for. */
  stepId: string
  /**
   * What the reviewer currently SEES, one entry per REWRITABLE THING: every
   * drafted question of a typed payload, and the whole body of a prose
   * artifact. A hand edit wins over the artifact it replaced. The rewrite takes
   * those as input and then wins over them, so editing by hand and editing by
   * chat compose in one direction.
   *
   * Read at call time, never captured, so a save made between arming and
   * sending still travels.
   */
  readStagedQuestions: () => Array<string>
  /**
   * Acts on a completed tail. Safe to call after the gate has moved on.
   *
   * `instruction` is the reader's message verbatim: on `revision` it is what
   * the writer step is redone with, so it travels to the server rather than
   * being interpreted here. The gate does not write questions and never did.
   */
  applyRevision: (
    tail: GateRevisionTail | null | undefined,
    instruction: string,
  ) => GateRevisionOutcome
  /**
   * Approve the parked gate with whatever is currently staged. Same payload
   * the dock button sends. Chat "continue" / "go" call this so edits are not
   * dropped.
   */
  approve: () => boolean
}

/* ------------------------------------------------------------------------ */
/* Drafted questions                                                         */
/* ------------------------------------------------------------------------ */

/**
 * The neutral follow-up stem per pairing kind.
 *
 * MIRRORED, NOT INVENTED. The source of truth is
 * `apps/backend/src/workflows/steps/twin-test/twin-test.constants.ts`
 * (`TWIN_TEST_PAIRED_OE_STEMS`), which itself mirrors `arc_registry.py:330`.
 * It is copied here because the reviewer is being asked to approve the words a
 * twin will actually be read, and a card that prints `reason` instead of the
 * stem is showing them a slug rather than a question. A kind that is missing
 * from this map renders no follow-up line at all, which is honest: an unknown
 * kind resolves to "no linked open end" server-side too.
 */
const GATE_PAIRED_OE_STEMS: Readonly<Record<string, string>> = {
  reason: "What's the main reason for your answer?",
  barrier: 'What would most hold you back?',
  drivers: 'What specifically drove your ratings?',
  importance: 'Of everything you just rated, what matters most to you?',
  gap: "What's missing or what would you change?",
  unlock: 'What would make the next step worth it for you?',
}

/** The answer scale, when the payload named a numeric one. */
export interface GateQuestionScale {
  min: number
  max: number
  /** Anchor wording, or null when the payload did not carry it. */
  minLabel: string | null
  maxLabel: string | null
}

export interface GateQuestionColumn {
  id: string
  label: string
  min: number | null
  max: number | null
  /**
   * Optional endpoint anchors. Independent ends — one-sided is legitimate
   * (unlike min/max). Null when absent/blank; never invent an anchor.
   */
  minLabel: string | null
  maxLabel: string | null
}

export interface GateQuestionRow {
  id: string
  label: string
}

/**
 * One version of a question's stimulus: the thing a panel is actually shown.
 *
 * MIRRORS `readVersionList` IN THE SEAM (`twin-test-spec.mapper.ts`). Each entry
 * needs an id, a label and content there, and fewer than two entries is not a
 * comparison and arms nothing, so a lone version's content is discarded before
 * any twin reads it. The gate renders what will run, so it reads them the same
 * way: two or more, or none.
 */
export interface GateQuestionVersion {
  label: string
  /** The stimulus text the panel is exposed to. Never truncated by the card. */
  content: string
}

/** One drafted question, as a card can render it without further lookups. */
export interface GateQuestion {
  title: string | null
  /** The question as the panel reads it. Never truncated by the card. */
  prompt: string
  questionType: string | null
  scale: GateQuestionScale | null
  /**
   * The answer options, for the three choice types. Empty when the payload
   * declared none, which is every other type.
   *
   * They are HALF THE QUESTION: the craft of a single_select is mostly in
   * whether its options are balanced and exhaustive, and a reviewer shown only
   * the prompt is approving an instrument they cannot see.
   */
  choices: Array<string>
  /** Measure / category columns for matrix types. Null elsewhere. */
  columns: Array<GateQuestionColumn> | null
  /** Stimulus / statement rows for matrix types. Null elsewhere. */
  rows: Array<GateQuestionRow> | null
  /**
   * The versions this question compares, or null when it compares nothing. Two
   * or more only: see `GateQuestionVersion`.
   */
  versions: Array<GateQuestionVersion> | null
  /** The follow-up as the twin will be asked it, already resolved from the kind. */
  pairedOeStem: string | null
}

/**
 * The inside of the first fenced block, or the body as it stands.
 *
 * The producer writes the payload as a fenced ```json block, but a body that
 * arrived unfenced is the same payload and must parse the same way: the gate is
 * the last stop before somebody approves spending, so it reads what is there
 * rather than what it hoped for.
 */
const FENCED_BLOCK = /(```[a-zA-Z]*\s*\n)([\s\S]*?)(```)/

/**
 * The body split around its first fenced block, so a payload can be read AND
 * written back into the same wrapper it arrived in.
 *
 * `unfence` throws the wrapper away, which is all a reader needs. A revision
 * edits one field of the payload and puts the rest back untouched, so it needs
 * the fence markers as well.
 */
function splitFencedBody(body: string): {
  open: string
  inner: string
  close: string
} {
  const fenced = FENCED_BLOCK.exec(body)
  if (!fenced) {
    return { open: '', inner: body, close: '' }
  }

  return {
    open: body.slice(0, fenced.index) + fenced[1],
    inner: fenced[2],
    close: fenced[3] + body.slice(fenced.index + fenced[0].length),
  }
}

function unfence(body: string): string {
  return splitFencedBody(body).inner.trim()
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function readScale(value: unknown): GateQuestionScale | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const candidate = value as Record<string, unknown>
  const min = candidate.min
  const max = candidate.max
  if (typeof min !== 'number' || typeof max !== 'number') {
    return null
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null
  }

  return {
    min,
    max,
    minLabel: readString(candidate.minLabel),
    maxLabel: readString(candidate.maxLabel),
  }
}

/**
 * The declared answer options, in the order they will be shown.
 *
 * `options` is read as well as `choices` because the seam reads both
 * (`readChoices` in `twin-test-spec.mapper.ts`), and a gate that knew only one
 * name would show an empty question for a payload the run will happily ask.
 * Blank entries are dropped rather than rendered as empty cells; the seam
 * refuses them outright, so nothing that survives here can mislead.
 */
function readChoiceList(value: unknown): Array<string> {
  if (!Array.isArray(value)) {
    return []
  }
  return value
    .map((entry) => readString(entry))
    .filter((entry): entry is string => entry !== null)
}

/**
 * The versions a question compares, or null when it compares nothing.
 *
 * Fewer than two is not a comparison and arms no monadic pass, so it renders as
 * no versions at all rather than as one: showing a single "version" would
 * promise the reviewer an exposure the run never makes. An entry missing its
 * label or content takes the whole list down, because a version the reviewer
 * cannot read is a version they cannot approve, and the seam refuses that same
 * payload anyway.
 */
function readVersionList(value: unknown): Array<GateQuestionVersion> | null {
  if (!Array.isArray(value) || value.length < 2) {
    return null
  }

  const versions: Array<GateQuestionVersion> = []
  for (const entry of value) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return null
    }
    const candidate = entry as Record<string, unknown>
    const label = readString(candidate.label)
    const content = readString(candidate.content)
    if (!label || !content) {
      return null
    }
    versions.push({ label, content })
  }

  return versions
}

function readColumns(value: unknown): Array<GateQuestionColumn> | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null
  }
  const columns: Array<GateQuestionColumn> = []
  for (let index = 0; index < value.length; index += 1) {
    const entry = value[index]
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return null
    }
    const candidate = entry as Record<string, unknown>
    const label = readString(candidate.label) ?? readString(candidate.title)
    if (!label) {
      return null
    }
    const id =
      readString(candidate.id) ??
      (index < 26
        ? `c${String.fromCharCode(97 + index)}`
        : `col_${index + 1}`)
    const hasMin =
      typeof candidate.min === 'number' && Number.isFinite(candidate.min)
    const hasMax =
      typeof candidate.max === 'number' && Number.isFinite(candidate.max)
    if (hasMin !== hasMax) {
      return null
    }
    columns.push({
      id,
      label,
      min: hasMin ? (candidate.min as number) : null,
      max: hasMax ? (candidate.max as number) : null,
      minLabel:
        readString(candidate.minLabel) ??
        readString(candidate.min_label) ??
        null,
      maxLabel:
        readString(candidate.maxLabel) ??
        readString(candidate.max_label) ??
        null,
    })
  }
  return columns
}

function readRows(value: unknown): Array<GateQuestionRow> | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null
  }
  const rows: Array<GateQuestionRow> = []
  for (let index = 0; index < value.length; index += 1) {
    const entry = value[index]
    if (typeof entry === 'string') {
      const label = entry.trim()
      if (!label) {
        return null
      }
      rows.push({
        id:
          index < 26
            ? `r${String.fromCharCode(97 + index)}`
            : `row_${index + 1}`,
        label,
      })
      continue
    }
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return null
    }
    const candidate = entry as Record<string, unknown>
    const label = readString(candidate.label) ?? readString(candidate.title)
    if (!label) {
      return null
    }
    const id =
      readString(candidate.id) ??
      (index < 26
        ? `r${String.fromCharCode(97 + index)}`
        : `row_${index + 1}`)
    rows.push({ id, label })
  }
  return rows
}

function readQuestion(value: unknown): GateQuestion | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const candidate = value as Record<string, unknown>
  const prompt = readString(candidate.prompt) ?? ''
  const questionType = readString(candidate.questionType)
  // Blank add-question cards ship with `questionType` and an empty prompt.
  // Require one of the two so a random object array still fails closed.
  if (!prompt && !questionType) {
    return null
  }

  // A custom stem is the author's own wording and wins over the default for
  // the kind. Neither one present means no follow-up to show.
  //
  // Stem/kind aliases (incl. snake_case) match backend
  // `TWIN_TEST_FOLLOW_UP_*_KEYS` — see `gateFollowUpKeys.ts`.
  const customStem = readFollowUpStem(candidate)
  const kind = readFollowUpKind(candidate)
  const defaultStem = kind ? (GATE_PAIRED_OE_STEMS[kind] ?? null) : null

  const columns = readColumns(candidate.columns)
  const rows = readRows(candidate.rows)
  return {
    title: readString(candidate.title),
    prompt,
    questionType,
    scale: readScale(candidate.scale),
    choices:
      columns && columns.length > 0
        ? []
        : readChoiceList(candidate.choices ?? candidate.options),
    columns,
    rows,
    versions: readVersionList(candidate.versions),
    pairedOeStem: customStem ?? defaultStem,
  }
}

/**
 * The drafted questions inside an artifact body, or null if it is not one.
 *
 * NULL IS THE IMPORTANT RETURN for non-questions payloads. Reports, briefs and
 * every other artifact travel through the same card, and a gate that cannot
 * render is a gate nobody can approve, so anything this does not recognise as a
 * questions payload falls back to the markdown rendering rather than throwing:
 * a bare array, a `{ questions: [...] }` object, fenced or not, is a questions
 * payload (including a valid empty list), and everything else, including a
 * half-written one, is prose.
 */
export function parseGateQuestions(body: string): Array<GateQuestion> | null {
  const source = unfence(body)
  if (!source.startsWith('{') && !source.startsWith('[')) {
    return null
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(source)
  } catch {
    return null
  }

  const raw = Array.isArray(parsed)
    ? parsed
    : parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>).questions
      : null

  // Valid empty list mounts the list/empty CTA so add-after-remove works.
  // Missing or non-array `questions` still falls back to markdown.
  if (!Array.isArray(raw)) {
    return null
  }

  if (raw.length === 0) {
    return []
  }

  const questions: Array<GateQuestion> = []
  for (const entry of raw) {
    const question = readQuestion(entry)
    // All or nothing. Rendering four of six questions and silently dropping
    // two is the one outcome a reviewer cannot detect.
    if (!question) {
      return null
    }
    questions.push(question)
  }

  return questions
}

/* ------------------------------------------------------------------------ */
/* What a chat revision may replace                                          */
/* ------------------------------------------------------------------------ */

/**
 * ONE THING A REVISION TURN MAY REWRITE.
 *
 * The unit is the QUESTION, not the artifact, and that distinction is the whole
 * reason this exists. The twin-test writer emits ONE artifact whose body is
 * `{questions: [...]}`, so a revision that staged artifact bodies handed the
 * model a whole JSON document as "question 1", got prose back, and replaced the
 * typed payload with it. The count check passed (one artifact, one body), the
 * card fell back to markdown, and the post-gate strict read then resolved the
 * whole thing as a single typeless question and ran it at full panel size.
 * Several approved questions collapsed into one garbage open end, through the
 * feature meant to refine them.
 *
 * So: a typed payload contributes one slot PER QUESTION, carrying that
 * question's prompt and nothing else, and the rewrite is spliced back into the
 * entry it came from. A prose artifact — a brief, a report — contributes one
 * slot carrying the whole body, which is what it always was.
 */
export interface GateRevisionSlot {
  /** Position in the gate's flattened artifact list. */
  artifactIndex: number
  /** Position within that artifact's question list, or null for a whole body. */
  questionIndex: number | null
  /** What the model is shown and asked to return a replacement for. */
  text: string
}

/**
 * The questions inside one body, as raw entries that can be written back.
 *
 * Deliberately NOT `parseGateQuestions`: that one normalises for rendering, and
 * a revision must put back every field it did not touch, including ones this
 * file has never heard of. `entries` is null when the body is a typed payload
 * whose questions cannot be read, which is the one case that must contribute no
 * slots at all: it renders as prose, so staging it as prose is exactly the
 * corruption above.
 */
interface TypedGateBody {
  /** The parsed payload, mutable and ready to be serialised back. */
  value: unknown
  entries: Array<Record<string, unknown>> | null
}

function readTypedBody(body: string): TypedGateBody | null {
  const source = unfence(body)
  if (!source.startsWith('{') && !source.startsWith('[')) {
    return null
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(source)
  } catch {
    return null
  }

  let raw: unknown
  if (Array.isArray(parsed)) {
    raw = parsed
  } else if (parsed && typeof parsed === 'object') {
    const candidate = parsed as Record<string, unknown>
    const carriesQuestions = 'questions' in candidate
    const carriesCaveat = 'noMatches' in candidate || 'noMatch' in candidate
    if (!carriesQuestions && !carriesCaveat) {
      // Some other JSON artifact. It has no questions to revise one of, so it
      // behaves as it always did: one slot, the whole body.
      return null
    }
    raw = candidate.questions
  } else {
    return null
  }

  if (!Array.isArray(raw) || raw.length === 0) {
    return { value: parsed, entries: null }
  }

  const entries: Array<Record<string, unknown>> = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return { value: parsed, entries: null }
    }
    const candidate = entry as Record<string, unknown>
    if (!readString(candidate.prompt)) {
      return { value: parsed, entries: null }
    }
    entries.push(candidate)
  }

  return { value: parsed, entries }
}

/**
 * Everything a rewrite may replace, in the order the model is shown it.
 *
 * `bodies` is the gate's cards as they STAND — a hand edit already applied —
 * one per artifact, in card order.
 */
export function planGateRevision(
  bodies: Array<string>,
): Array<GateRevisionSlot> {
  const slots: Array<GateRevisionSlot> = []

  bodies.forEach((body, artifactIndex) => {
    const typed = readTypedBody(body)
    if (!typed) {
      slots.push({ artifactIndex, questionIndex: null, text: body })
      return
    }
    if (!typed.entries) {
      // A typed payload nobody can read a question out of: a half-written one,
      // or a body that carries only the C3 caveat. There is no question to
      // rewrite, and replacing it wholesale is the defect this file exists to
      // prevent, so it is left alone. It is still editable by hand.
      return
    }
    typed.entries.forEach((entry, questionIndex) => {
      slots.push({
        artifactIndex,
        questionIndex,
        text: String(entry.prompt).trim(),
      })
    })
  })

  return slots
}

/* ------------------------------------------------------------------------ */
/* The no-match caveat (C3)                                                  */
/* ------------------------------------------------------------------------ */

/**
 * One question the mapping seam refused, exactly as C3 freezes it.
 *
 * It travels in the SAME artifact body as `questions`, as a sibling key, and it
 * exists because a silent drop is the one outcome a reviewer cannot detect: a
 * writer asked for something the engine cannot run, the seam refused on shape,
 * and the reviewer needs to see that the question they read in the conversation
 * is not among the ones they are about to pay for.
 *
 * It is NEVER approvable and NEVER counted in the spend line. Both are enforced
 * where they matter rather than trusted here: the card renders these outside the
 * question list and outside its count, and `runSpendLine` counts only
 * `questions`.
 */
export interface GateNoMatch {
  title: string | null
  /** The question as the writer wrote it. Shown in full, like a runnable one. */
  prompt: string
  /** The `questionType` that was asked for, when the payload named one. */
  askedType: string | null
  /** What the engine can run. Empty when the payload did not carry the list. */
  supportedTypes: Array<string>
  /**
   * Structural, composed by the seam from facts. Never a rewrite of the question.
   *
   * The backend carries this as `detail`, with its own `reason` field holding a
   * grouping CODE (`unsupported_type`, `ambiguous_type`, ...) rather than prose.
   * An earlier contract had a single prose `reason`, so that is read as a
   * fallback -- but `detail` wins, or a person would be shown an enum.
   */
  reason: string | null
}

/* NO `reframedAs`, DELETED RATHER THAN LEFT PARSED (F8). The frozen contract
   promised the repair pass would hand back the question it rewrote, and the
   seam cannot: a repair re-invokes the writer and keeps the second attempt
   WHOLE, so nothing can attribute a repaired question to the one it replaced.
   The field was never emitted. Parsing and rendering it was a card that could
   only ever be blank, and a reader of this file could not tell that from a
   feature waiting on data. */

function readStringList(value: unknown): Array<string> {
  if (!Array.isArray(value)) {
    return []
  }
  return value
    .map((entry) => readString(entry))
    .filter((entry): entry is string => entry !== null)
}

function readNoMatch(value: unknown): GateNoMatch | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const candidate = value as Record<string, unknown>
  const prompt = readString(candidate.prompt)
  if (!prompt) {
    return null
  }

  return {
    title: readString(candidate.title),
    prompt,
    askedType: readString(candidate.askedType),
    supportedTypes: readStringList(candidate.supportedTypes),
    reason: readString(candidate.detail) ?? readString(candidate.reason),
  }
}

/**
 * The refused questions inside an artifact body, or null if there are none.
 *
 * Deliberately independent of `parseGateQuestions`: a payload whose questions
 * all survived carries no `noMatch` at all, and a payload that is prose carries
 * neither. Reading them separately means a malformed caveat can never take the
 * runnable questions down with it, which would turn an honest refusal into an
 * unapprovable gate.
 *
 * Skips entries it cannot read rather than failing the whole list, the opposite
 * of `parseGateQuestions`: dropping a runnable question hides something the
 * reviewer is paying for, whereas dropping an unreadable caveat only loses an
 * explanation for something that was never going to run.
 */
export function parseGateNoMatches(body: string): Array<GateNoMatch> | null {
  const source = unfence(body)
  if (!source.startsWith('{')) {
    return null
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(source)
  } catch {
    return null
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null
  }

  /* `noMatches` is what the backend seam emits (`buildTwinTestGatePayload`).
     `noMatch` was the frozen contract's name and is still read, because a body
     written before the seam shipped is still a body somebody has to approve.
     Reading only one of the two renders an empty caveat over real refusals,
     which is the exact failure this list exists to prevent. */
  const candidateList = (parsed as Record<string, unknown>).noMatches
  const raw = Array.isArray(candidateList)
    ? candidateList
    : (parsed as Record<string, unknown>).noMatch
  if (!Array.isArray(raw) || raw.length === 0) {
    return null
  }

  const entries = raw
    .map((entry) => readNoMatch(entry))
    .filter((entry): entry is GateNoMatch => entry !== null)

  return entries.length > 0 ? entries : null
}

/**
 * Questions (or a no-match caveat) belong in the composer dock. Everything
 * else is already in the thread, so the gate there is only the decision.
 */
export function isGateQuestionsDockBody(body: string): boolean {
  return parseGateQuestions(body) !== null || parseGateNoMatches(body) !== null
}

/**
 * Whether the open gate should sit in `ChatComposer.aboveSlot`.
 *
 * True only when a pending gate is showing a questions (or no-match) payload:
 * that preview has nowhere else to live. Scan / brief / report gates already
 * posted their bodies in the thread, so Continue stays there too.
 */
export function pendingGateDocksOnComposer(
  run: WorkflowRun | null,
  template: WorkflowTemplate | null,
): boolean {
  if (!run) {
    return false
  }

  const pendingGate = run.steps.find(
    (step) => step.stepKind === 'gate' && step.state === 'awaiting_approval',
  )
  if (!pendingGate) {
    return false
  }

  return collectGateArtifacts(run, pendingGate, template).some((artifact) =>
    isGateQuestionsDockBody(artifact.body),
  )
}
